import {useInfiniteQuery} from '@tanstack/react-query';
import {fetchRecords} from '../services/ReconciliationReportsService';
import {useEffect, useState} from 'react';
import {BillingChannel} from '../types.ts';

// FNP-535: read-only module. No create/update/delete mutations — the ChannelReconciler
// (Job B) owns the lifecycle. This hook is list-fetch only.
//
// Unlike the sibling sweep modules (binding-drift has no filter), listReconciliationReports
// REQUIRES a channel (the channel-runStartedAt-index partition key). So instead of a
// free-form filter store, this module carries a single module-scoped CHANNEL selection
// (default STRIPE), shared between the channel selector in the header and the list hook
// via the same listener pattern used by the filter stores elsewhere.
const DEFAULT_CHANNEL: BillingChannel = 'STRIPE';

let globalChannel: BillingChannel = DEFAULT_CHANNEL;
const channelListeners: Set<(channel: BillingChannel) => void> = new Set();

const notifyChannelChange = (channel: BillingChannel) => {
    globalChannel = channel;
    channelListeners.forEach(listener => listener(channel));
};

export const useReconciliationReportsChannel = () => {
    const [channel, setChannel] = useState<BillingChannel>(globalChannel);

    useEffect(() => {
        const listener = (newChannel: BillingChannel) => setChannel(newChannel);
        channelListeners.add(listener);
        return () => {
            channelListeners.delete(listener);
        };
    }, []);

    const handleSetChannel = (newChannel: BillingChannel) => {
        notifyChannelChange(newChannel);
    };

    return {channel, handleSetChannel};
};

export const useReconciliationReports = () => {
    const {channel} = useReconciliationReportsChannel();
    const [limit] = useState(10);

    // INFINITE QUERY — channel is part of the cache key, so switching the channel
    // refetches automatically. The channel-runStartedAt-index is ordered DESC
    // server-side (newest run first).
    const reportsQuery = useInfiniteQuery({
        queryKey: ['reconciliationReports', channel, limit],
        queryFn: ({pageParam}: { pageParam: string | null }) =>
            fetchRecords({channel, limit, nextToken: pageParam}),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null
    });

    const fetchRecordsAction = () => {
        if (reportsQuery.hasNextPage) {
            reportsQuery.fetchNextPage();
        }
    };

    const reload = () => {
        reportsQuery.refetch();
    };

    // The service layer guarantees page.items is a real array even on a GraphQL-level
    // error; filter(Boolean) keeps an undefined row out of the table as defense-in-depth.
    const allItems = (reportsQuery.data?.pages?.flatMap(page => page?.items ?? []) || []).filter(Boolean);

    return {
        items: allItems,
        visibleItems: allItems,
        loading: reportsQuery.isLoading,
        visibleCount: allItems.length,
        totalCount: allItems.length,
        nextToken: reportsQuery.hasNextPage ? 'has-more' : null,
        fetchRecords: fetchRecordsAction,
        reload,
    };
};
